import { useQueries } from "@tanstack/react-query";
import { QUERIES_KEYS } from "queries";
import { useCategories } from "api/categories";
import { categoriesTermsApi } from "./categoriesTermsApi";
import { CategoryApi } from "api/categories/categoriesApi";

/** The category and its ancestors, nearest first (a cycle-safe walk up parentId). */
export const categoryChain = (categories: CategoryApi[], categoryId?: number): CategoryApi[] => {
  const chain: CategoryApi[] = [];
  let current = categories.find((c) => c.id === categoryId);
  while (current && !chain.some((c) => c.id === current!.id)) {
    chain.push(current);
    current = current.parentId != null ? categories.find((c) => c.id === current!.parentId) : undefined;
  }
  return chain;
};

/**
 * Terms a student accepts when signing up for a course: those of its category and of every ancestor category
 * (the API's FormsRepository walks the same chain). Grouped per category, nearest first.
 */
export const useInheritedCategoryTerms = (categoryId?: number) => {
  const { categories, isLoading: loadingCategories } = useCategories();
  const chain = categoryChain(categories, categoryId);
  const results = useQueries({
    queries: chain.map((c) => ({
      queryKey: QUERIES_KEYS.CATEGORY_TERMS(c.id),
      queryFn: () => categoriesTermsApi.fetchTerms(c.id),
    })),
  });
  return {
    isLoading: loadingCategories || results.some((r) => r.isLoading),
    groups: chain
      .map((category, i) => ({ category, terms: results[i]?.data?.data?.terms ?? [] }))
      .filter((g) => g.terms.length > 0),
  };
};
